import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { some } from 'lodash';
import type { RootState } from 'types/redux/state';

/**
 * Interface for spotlight style properties
 */
interface SpotlightStyles {
  width?: string;
  height?: string;
  transform?: string;
  position?: 'absolute' | 'relative' | 'fixed' | 'sticky';
  top?: number | string;
  left?: number | string;
  boxShadow?: string;
  borderRadius?: string;
  opacity?: number;
}

/**
 * Interface for containing rectangle dimensions
 */
interface ContainingRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Interface for dimensions
 */
interface Dimensions {
  width: number;
  height: number;
  left: number;
  top: number;
}


/**
* @module Spotlight
* @description A React component that creates a highlighted focus area around 
* specified elements during tutorials, with smooth transitions and responsive positioning.
* 
* @component
* @description
* Creates visual focus by:
* 1. Spotlight Positioning:
*    - Tracks single or multiple target elements
*    - Calculates containing rectangle
*    - Handles dynamic target changes
*    - Maintains responsive positioning
* 
* 2. Animation Management:
*    - Smooth transitions between targets
*    - Handles element mutations
*    - Controls movement states
*    - Manages animation timing
* 
* 3. DOM Observation:
*    - Watches for attribute changes
*    - Tracks class/style mutations
*    - Updates position after animations
*    - Cleans up observers
* 
* @example
* // Single target
* // Redux state:
* {
*   ui: {
*     spotlightTarget: 'menuItem1'
*   }
* }
* 
* // Multiple targets
* {
*   ui: {
*     spotlightTarget: ['menuItem1', 'menuItem2']
*   }
* }
*/
const Spotlight: React.FC = () => {
  const [spotlightStyles, setSpotlightStyles] = useState<SpotlightStyles>({});
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [prevTarget, setPrevTarget] = useState<string | string[] | null>(null);
  const updateTimeout = useRef<number | null>(null);
  
  // Get spotlight target and tutorial modal state from Redux
  const target = useSelector((state: RootState) => state.ui.spotlightTarget);
  const isTutorialOpen = useSelector((state: RootState) => 
    state.ui.isModalOpen && some(state.ui.modals, modal => modal.modalName === 'tutorial')
  );

  /**
   * Update the spotlight position based on target element(s)
   */
  const updateSpotlightPosition = (): void => {
    // Handle single string or array of target IDs
    const targetIds = Array.isArray(target) ? target : [target];
    
    // Get all valid target elements
    const targetElements = targetIds
      .map(id => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (targetElements.length === 0) return;

    // Get DOMRect for each element
    const rects = targetElements.map(element => element.getBoundingClientRect());

    // Calculate the containing rectangle
    const containingRect = rects.reduce((container, rect) => ({
      left: Math.min(container.left, rect.left),
      top: Math.min(container.top, rect.top),
      right: Math.max(container.right, rect.right),
      bottom: Math.max(container.bottom, rect.bottom)
    }), {
      left: Infinity,
      top: Infinity,
      right: -Infinity,
      bottom: -Infinity
    } as ContainingRect);

    // Convert to dimensions
    const dimensions: Dimensions = {
      width: containingRect.right - containingRect.left,
      height: containingRect.bottom - containingRect.top,
      left: containingRect.left,
      top: containingRect.top
    };
    
    // Set the spotlight styles
    setSpotlightStyles({
      width: `${dimensions.width + 8}px`,
      height: `${dimensions.height + 8}px`,
      transform: `translate(${dimensions.left - 4}px, ${dimensions.top - 4}px)`,
      position: 'absolute',
      top: 0,
      left: 0,
      boxShadow: 'inset #fff 0 0 0 0px, var(--app-medium-gray) 0 0 0 2000px',
      borderRadius: '5px',
      opacity: 0.6
    });
  };

  useEffect(() => {
    if (!target || !isTutorialOpen) return;

    // Track target changes to trigger movement animation
    if (prevTarget !== target) {
      setIsMoving(true);
      setPrevTarget(target);
    }

    // Clear any existing update timeout
    if (updateTimeout.current) {
      window.clearTimeout(updateTimeout.current);
    }

    // Initial position update
    updateSpotlightPosition();

    // Set up mutation observer to watch for menu changes
    const targetId = Array.isArray(target) ? target[0] : target;
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
          // Update after a short delay to allow animations to complete
          updateTimeout.current = window.setTimeout(updateSpotlightPosition, 300);
        }
      });
    });

    observer.observe(targetElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      subtree: true
    });

    // Handle movement animation
    if (isMoving) {
      const timer = window.setTimeout(() => setIsMoving(false), 300);
      return () => {
        window.clearTimeout(timer);
        observer.disconnect();
        if (updateTimeout.current) {
          window.clearTimeout(updateTimeout.current);
        }
      };
    }

    // Cleanup function
    return () => {
      observer.disconnect();
      if (updateTimeout.current) {
        window.clearTimeout(updateTimeout.current);
      }
    };
  }, [target, isTutorialOpen, prevTarget, isMoving]);

  // Calculate overlay positions based on spotlight dimensions
  const overlayStyles = React.useMemo(() => {
    if (!spotlightStyles.transform) return null;
    
    // Parse the transform to get position
    const match = spotlightStyles.transform.match(/translate\(([\d.-]+)px,\s*([\d.-]+)px\)/);
    if (!match) return null;
    
    const left = parseFloat(match[1]) - 4;
    const top = parseFloat(match[2]) - 4;
    const width = parseFloat(spotlightStyles.width || '0');
    const height = parseFloat(spotlightStyles.height || '0');
    
    return {
      top: { 
        position: 'fixed' as const, 
        top: 0, 
        left: 0, 
        right: 0, 
        height: `${top}px`,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none' as const,
        zIndex: 1250
      },
      bottom: {
        position: 'fixed' as const,
        top: `${top + height}px`,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none' as const,
        zIndex: 1250
      },
      left: {
        position: 'fixed' as const,
        top: `${top}px`,
        left: 0,
        width: `${left}px`,
        height: `${height}px`,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none' as const,
        zIndex: 1250
      },
      right: {
        position: 'fixed' as const,
        top: `${top}px`,
        left: `${left + width}px`,
        right: 0,
        height: `${height}px`,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none' as const,
        zIndex: 1250
      }
    };
  }, [spotlightStyles]);

  return (
    <Fragment>
      {target && overlayStyles && (
        <>
          <div style={overlayStyles.top} />
          <div style={overlayStyles.bottom} />
          <div style={overlayStyles.left} />
          <div style={overlayStyles.right} />
        </>
      )}
    </Fragment>  
  );
};

export default Spotlight;
